import React, { useEffect, useState, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle2, Clock, AlertCircle, FileCheck, ArrowRight, Send, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

// Status configuration
const STATUS_CONFIG = {
  SUBMITTED: {
    label: 'Submitted',
    color: 'success',
    badgeClass: 'bg-green-500',
    borderClass: 'border-l-green-500',
    icon: CheckCircle2,
  },
  NEED_INFO: {
    label: 'Waiting for Info',
    color: 'warning',
    badgeClass: 'bg-yellow-500',
    borderClass: 'border-l-yellow-500',
    icon: AlertCircle,
  },
  READY_FOR_REVIEW: {
    label: 'Ready for Review',
    color: 'success',
    badgeClass: 'bg-green-500',
    borderClass: 'border-l-green-500',
    icon: FileCheck,
  },
  SUBMITTED_TO_IRS: {
    label: 'Submitted to IRS',
    color: 'success',
    badgeClass: 'bg-green-600',
    borderClass: 'border-l-green-600',
    icon: Send,
  },
  DRAFT: {
    label: 'Draft',
    color: 'muted',
    badgeClass: 'bg-gray-500',
    borderClass: 'border-l-gray-500',
    icon: Clock,
  },
};

// Timeline steps order
const TIMELINE_STEPS = ['SUBMITTED', 'NEED_INFO', 'READY_FOR_REVIEW', 'SUBMITTED_TO_IRS'];

export default function LLCFilingStatusPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [llcFormations, setLlcFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentLLC, setCurrentLLC] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to latest message within container
  const scrollToBottom = () => {
    const scrollArea = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
    if (scrollArea) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  // Fetch messages for current LLC
  const fetchMessages = async (llcId) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${backendUrl}/api/llc-formations/${llcId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    }
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentLLC) return;

    try {
      setSendingMessage(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${backendUrl}/api/llc-formations/${currentLLC.id}/messages`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Poll for new messages every 10 seconds when LLC is not DRAFT
  useEffect(() => {
    if (currentLLC && currentLLC.status !== 'DRAFT') {
      fetchMessages(currentLLC.id);
      
      const interval = setInterval(() => {
        fetchMessages(currentLLC.id);
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [currentLLC]);

  useEffect(() => {
    const fetchLLCs = async () => {
      try {
        setLoading(true);
        const backendUrl = getBackendUrl();
        const token = localStorage.getItem('access_token');
        
        const response = await axios.get(`${backendUrl}/api/llc-formations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const data = response.data || [];
        // Filter out DRAFT LLCs
        const nonDraftLLCs = data.filter(llc => llc.status !== 'DRAFT');
        setLlcFormations(nonDraftLLCs);
        
        // Set the first non-draft LLC as current
        if (nonDraftLLCs.length > 0) {
          setCurrentLLC(nonDraftLLCs[0]);
        }
      } catch (error) {
        console.error('Error fetching LLCs:', error);
        toast.error('Failed to load LLC formations');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchLLCs();
    }
  }, [user]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get step status based on current LLC status
  const getStepStatus = (stepKey, currentStatus) => {
    const currentIndex = TIMELINE_STEPS.indexOf(currentStatus);
    const stepIndex = TIMELINE_STEPS.indexOf(stepKey);
    
    // Special case: SUBMITTED step is always completed once LLC is submitted
    if (stepKey === 'SUBMITTED' && currentIndex >= 0) {
      return 'completed';
    }
    
    if (stepIndex < currentIndex) {
      return 'completed';
    } else if (stepIndex === currentIndex) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  // Build timeline steps with dynamic data
  const getTimelineSteps = () => {
    if (!currentLLC) return [];

    return TIMELINE_STEPS.map((stepKey) => {
      const config = STATUS_CONFIG[stepKey];
      const status = getStepStatus(stepKey, currentLLC.status);
      
      return {
        label: config.label,
        status: status,
        icon: config.icon,
        showMessage: stepKey === 'NEED_INFO' && status === 'current',
        message: currentLLC.status_message,
      };
    });
  };

  // Handle LLC selection from dropdown
  const handleLLCChange = (llcId) => {
    const selected = llcFormations.find((llc) => llc.id === llcId);
    setCurrentLLC(selected);
  };

  if (!user) return <div>Loading...</div>;

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="LLC Filing Status" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!currentLLC) {
    return (
      <DashboardLayout role={user.role} title="LLC Filing Status" user={user}>
        <div className="min-h-screen flex items-center justify-center p-6">
          <Card className="max-w-2xl w-full">
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Clock className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No LLC Filings Yet</h3>
                <p className="text-muted-foreground mb-4">
                  You haven't submitted any LLC formations yet. Start a new LLC formation to track its progress here.
                </p>
                <Button onClick={() => navigate('/llc/create')} className="gap-2">
                  Start LLC Formation
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const timelineSteps = getTimelineSteps();
  const currentStatusConfig = STATUS_CONFIG[currentLLC.status] || STATUS_CONFIG.SUBMITTED;

  return (
    <DashboardLayout role={user.role} title="LLC Filing Status" user={user}>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Select LLC Filing */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Building2 className="h-5 w-5 text-primary" />
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Select LLC Filing</label>
                <Select value={currentLLC?.id} onValueChange={handleLLCChange}>
                  <SelectTrigger className="w-full md:w-[400px]">
                    <SelectValue placeholder="Select an LLC filing" />
                  </SelectTrigger>
                  <SelectContent>
                    {llcFormations.map((llc) => (
                      <SelectItem key={llc.id} value={llc.id}>
                        {llc.company_name || 'Unnamed LLC'} - {llc.state} ({llc.status?.replace('_', ' ')})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {currentLLC && (
          <>
            {/* Current Status Card */}
            <Card className={cn("card-elevated border-l-4", currentStatusConfig?.borderClass)}>
              <CardHeader>
                <CardTitle>Current Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className={cn("p-3 rounded-full", currentStatusConfig?.badgeClass)}>
                    {currentStatusConfig?.icon && <currentStatusConfig.icon className="h-6 w-6 text-white" />}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{currentStatusConfig?.label}</h3>
                    <p className="text-sm text-muted-foreground">
                      {currentLLC.company_name || 'Unnamed LLC'}
                    </p>
                  </div>
                </div>

                {/* Conversation with Agent */}
                {currentLLC.status !== 'DRAFT' && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <span>💬</span> Conversation with Agent
                    </h4>
                    <div className="border rounded-lg">
                      <ScrollArea className="h-[250px] p-4">
                        <div className="space-y-3">
                          {messages.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">
                              No messages yet
                            </p>
                          ) : (
                            messages.map((msg) => (
                              <div
                                key={msg.id}
                                className={cn(
                                  "flex",
                                  msg.sender_role === 'client' ? 'justify-end' : 'justify-start'
                                )}
                              >
                                <div
                                  className={cn(
                                    "max-w-[80%] rounded-lg p-3",
                                    msg.sender_role === 'client'
                                      ? 'bg-primary text-primary-foreground'
                                      : 'bg-muted'
                                  )}
                                >
                                  <p className="text-xs font-medium mb-1 opacity-70">
                                    {msg.sender_role === 'client' ? 'You' : msg.sender_name || 'Agent'}
                                  </p>
                                  <p className="text-sm">{msg.message}</p>
                                  <p className="text-xs mt-1 opacity-50">
                                    {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                          <div ref={messagesEndRef} />
                        </div>
                      </ScrollArea>
                      <div className="p-3 border-t">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            disabled={sendingMessage}
                          />
                          <Button 
                            onClick={handleSendMessage}
                            disabled={sendingMessage || !newMessage.trim()}
                            size="icon"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Filing Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Filing Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-0">
                  {timelineSteps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isLast = index === timelineSteps.length - 1;

                    return (
                      <div key={index} className="flex gap-4 relative">
                        {/* Icon and Connector */}
                        <div className="flex flex-col items-center">
                          <div
                            className={cn(
                              "rounded-full h-10 w-10 flex items-center justify-center z-10",
                              step.status === 'completed' && "bg-green-500",
                              step.status === 'current' && "bg-blue-500",
                              step.status === 'pending' && "bg-gray-300"
                            )}
                          >
                            <StepIcon
                              className={cn(
                                "h-5 w-5",
                                step.status === 'completed' && "text-white",
                                step.status === 'current' && "text-white",
                                step.status === 'pending' && "text-gray-600"
                              )}
                            />
                          </div>
                          {!isLast && (
                            <div
                              className={cn(
                                "w-0.5 h-16 -mt-1",
                                step.status === 'completed' ? "bg-green-500" : "bg-gray-300"
                              )}
                            />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 pb-8">
                          <h4
                            className={cn(
                              "font-semibold",
                              step.status === 'pending' && "text-muted-foreground"
                            )}
                          >
                            {step.label}
                          </h4>
                          {step.status === 'completed' && (
                            <p className="text-sm text-muted-foreground">
                              {formatDate(currentLLC.created_at)}
                            </p>
                          )}
                          {step.status === 'current' && (
                            <p className="text-sm text-muted-foreground">In Progress</p>
                          )}
                          {step.status === 'pending' && (
                            <p className="text-sm text-muted-foreground">Pending</p>
                          )}
                          {step.showMessage && step.message && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-sm text-yellow-800">{step.message}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Filing Details */}
            <Card>
              <CardHeader>
                <CardTitle>Filing Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Company Name</p>
                    <p className="font-medium">{currentLLC.company_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">State</p>
                    <p className="font-medium">{currentLLC.state || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Business Type</p>
                    <p className="font-medium">{currentLLC.business_type || 'LLC'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {currentLLC.created_at ? new Date(currentLLC.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
